import { Property } from '../types/property';
import { SearchParams } from '../types/search-params';
import { enhancedFetch } from '../utils/api-helpers';
import { extractOpenHouseData } from '../utils/open-house-utils';
import { convertSelectedPropertyTypesToApiString } from '../utils/property-class-cache';
import { filterZestimateDescriptions } from '../utils';

/**
 */
/**
 * Sale Property Search Parameters Interface - Updated for Zillow API
 */
export interface SalePropertySearchParams {
  // Location parameters
  location: string; // Location string for Zillow API
  address?: string; // For address-based search
  
  // Pagination
  page?: number;
  resultsPerPage?: number;
  
  // Search configuration
  status?: 'forSale' | 'forRent' | 'sold';
  sort?: 'relevance' | 'recentlyChanged' | 'daysOn' | 'price' | 'beds' | 'baths' | 'livingArea' | 'lotArea' | 'yearBuilt' | 'rentalPriorityScore';
  sortType?: 'asc' | 'desc';
  priceType?: 'listPrice' | 'monthlyPayment';
  
  // Price filters
  priceRange?: string; // Format: "minPrice,maxPrice" or "minPrice," or ",maxPrice"
  
  // HOA filters
  hoaFees?: number;
  includeNoHoaDataHomes?: boolean;
  
  // Bedroom/Bathroom filters
  bedrooms?: string; // Format: "min,max" or "min" or ",max"
  bathrooms?: string; // Format: "1" | "1.5" | "2" | "3" | "4"
  
  // Property type filters
  homeType?: string; // singleFamily, townhome, condo,apartment, land, multiFamily, manufactured
  
  // Size filters
  homeSize?: string; // Format: "min,max" in sqft
  lotSize?: string; // Format: "min,max" in sqft
  yearBuilt?: string; // Format: "min,max"
  
  // Amenity filters
  amenities?: string; // haveAirConditioning, havePool, onWaterfront
  parkingSpots?: number;
  haveGarage?: boolean;
  haveSingleStory?: boolean;
  basementTypes?: string; // finished, unfinished, partial
  
  // Search filters
  keywords?: string;
  listing?: string; // fsba, newConstruction, foreclosure, auction, foreclosed, preforeclosure, fsbo
  listingType?: 'agent' | 'other';
  listingStatus?: string; // comingSoon, priceReduction, acceptingBackupOffers, pending
  touring?: string; // openHouses, approvedVirtualTour
  timeOnZillow?: number;
  views?: string; // city, mountain, park, water
  seniorLiving?: string; // seniorCommunity
  space?: string; // room
}

/**
 * Convert UI property type labels to API homeType parameter for buy/sold
 * @param selectedPropertyTypes - Array of UI property type labels
 * @param category - 'buy' or 'sold'
 * @returns Comma-separated string for homeType parameter
 */
export function convertPropertyTypesForBuySold(selectedPropertyTypes: string[], category: 'buy' | 'sold'): string {
  return convertSelectedPropertyTypesToApiString(selectedPropertyTypes, category);
}

// Import centralized caching
import { propertyCache, generateCacheKey, cacheSaleProperties } from '../utils/centralized-cache';

// Cache for auto-complete results to reduce API calls
const autoCompleteCache = new Map<string, { result: string; timestamp: number }>();
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// (Removed) property details/tour caching
const propertyDetailsCache = new Map<string, { details: any; timestamp: number }>();
const DETAILS_CACHE_DURATION = 60 * 60 * 1000;

// Throttle mechanism to allow searches at regular intervals

// Cooldown to avoid immediate re-fetch storms after a timeout for the same query
const searchTimeoutCooldown = new Map<string, number>();

/**
 * Round lot size values to maximum 2 decimal places
 * This prevents excessive decimal places like "0.382300000 acres"
 */
function roundLotSize(lotSize: number | null | undefined): number | null {
  if (lotSize === null || lotSize === undefined) {
    return null;
  }
  
  // Round to maximum 2 decimal places for all values
  return Math.round(lotSize * 100) / 100;
}

/**
 * Clean up expired cache entries to prevent memory leaks
 */
function cleanupCache() {
  const now = Date.now();
  
  // Clean up auto-complete cache
  for (const [key, value] of autoCompleteCache.entries()) {
    if (now - value.timestamp > CACHE_DURATION) {
      autoCompleteCache.delete(key);
    }
  }
  
  // Clean up property details cache
  for (const [key, value] of propertyDetailsCache.entries()) {
    if (now - value.timestamp > DETAILS_CACHE_DURATION) {
      propertyDetailsCache.delete(key);
    }
  }
}

// Clean up cache every 5 minutes
setInterval(cleanupCache, 5 * 60 * 1000);

/**
 * Throttled search function to allow searches at regular intervals
 * This is better than debouncing for search functionality
 */
function throttleSearch<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  throttleMs: number = 100
): T {
  let lastCall = 0;
  let pendingCall: NodeJS.Timeout | null = null;
  
  return ((...args: any[]) => {
    const now = Date.now();
    
    if (now - lastCall >= throttleMs) {
      // Enough time has passed, execute immediately
      lastCall = now;
      return fn(...args);
    } else {
      // Schedule execution for when throttle period ends
      const delay = throttleMs - (now - lastCall);
      
      if (pendingCall) {
        clearTimeout(pendingCall);
      }
      
      return new Promise((resolve, reject) => {
        pendingCall = setTimeout(async () => {
          lastCall = Date.now();
          try {
            const result = await fn(...args);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        }, delay);
      });
    }
  }) as T;
}

// Removed virtual tour/details fetch utilities

// Removed virtual tour extraction

/**
 * Extract property data from search response and enhance with details data
 */
async function extractPropertyFromSearchData(zpid: string, basicData: any): Promise<Property> {
  // Check if this is address search data (has formattedChip structure)
  if (basicData.formattedChip) {
    return extractPropertyFromAddressData(zpid, basicData);
  }
  
  // Otherwise, use the standard location search extraction
  return extractPropertyFromLocationData(zpid, basicData);
}

/**
 * Extract property data from address search response (different data structure)
 */
function extractPropertyFromAddressData(zpid: string, basicData: any): Property {
  // Extract address information from formattedChip
  const address = basicData.formattedChip?.location?.[0]?.fullValue || 
                  basicData.address || 
                  'Address not available';
  
  // Extract city, state, zip from formattedChip location array
  let city = '';
  let state = '';
  let zipCode = '';
  
  if (basicData.formattedChip?.location?.[1]?.fullValue) {
    const cityStateZip = basicData.formattedChip.location[1].fullValue;
    const parts = cityStateZip.split(',').map((part: string) => part.trim());
    city = parts[0] || '';
    if (parts[1]) {
      const stateZipParts = parts[1].split(' ');
      state = stateZipParts[0] || '';
      zipCode = stateZipParts[1] || '';
    }
  } else {
    // Fallback to parsing the address string
    const addressParts = address.split(',').map((part: string) => part.trim());
    city = addressParts[1] || '';
    const stateZip = addressParts[2] || '';
    const stateZipParts = stateZip.split(' ');
    state = stateZipParts[0] || '';
    zipCode = stateZipParts[1] || '';
  }
  
  // Extract price from formattedChip
  let price = 0;
  if (basicData.formattedChip?.mainPrice?.value?.fullValue) {
    price = parseInt(basicData.formattedChip.mainPrice.value.fullValue.replace(/[$,]/g, ''));
  } else if (basicData.price) {
    price = typeof basicData.price === 'number' ? basicData.price : parseInt(basicData.price);
  }
  
  // Extract property details from formattedChip and resoFacts
  const bedrooms = parseInt(basicData.formattedChip?.quickFacts?.[0]?.value?.fullValue) || 
                   basicData.bedrooms || 
                   basicData.beds || 0;
  
  const bathrooms = parseInt(basicData.formattedChip?.quickFacts?.[1]?.value?.fullValue) || 
                   basicData.bathrooms || 
                   basicData.baths || 0;
  
  const squareFeet = parseInt(basicData.formattedChip?.quickFacts?.[2]?.value?.fullValue?.replace(/,/g, '')) || 
                     parseInt(basicData.livingAreaValue) || 
                     basicData.livingArea || 
                     basicData.squareFeet || 0;
  
  const yearBuilt = parseInt(basicData.resoFacts?.yearBuilt) || 
                    basicData.yearBuilt || 0;
  
  // Extract lot size - try multiple sources
  let lotSize: any = 0;
  let lotSizeUnit = '';
  
  if (basicData.resoFacts?.lotSize) {
    // Parse "7,013 sqft" or "0.32 Acres" format
    const lotSizeMatch = basicData.resoFacts.lotSize.match(/([\d,.]+)/);
    if (lotSizeMatch) {
      lotSize = parseFloat(lotSizeMatch[1].replace(/,/g, ''));
    }
    // Extract unit from the string (e.g., "sqft", "acres")
    const unitMatch = basicData.resoFacts.lotSize.match(/([a-zA-Z]+)/);
    if (unitMatch) {
      lotSizeUnit = unitMatch[1].toLowerCase();
    }
  } else if (basicData.lotAreaValue) {
    lotSize = basicData.lotAreaValue;
  } else if (basicData.lotSizeWithUnit?.lotSize) {
    lotSize = basicData.lotSizeWithUnit.lotSize;
    lotSizeUnit = basicData.lotSizeWithUnit.lotSizeUnit || '';
  } else if (basicData.lotArea) {
    lotSize = basicData.lotArea;
  } else if (basicData.lotSize) {
    lotSize = basicData.lotSize;
  }
  
  // Fallback unit from lotAreaUnits if provided by API
  if (!lotSizeUnit && basicData.lotAreaUnits) {
    lotSizeUnit = basicData.lotAreaUnits;
  }
  
  // Round the lot size to reasonable precision
  lotSize = roundLotSize(lotSize);
  
  // Extract photos - prioritize high-resolution photos
  let photos: string[] = [];
  
  // PRIORITY 1: Try photoUrlsHighRes first for high-resolution photos
  if (basicData.photoUrlsHighRes && Array.isArray(basicData.photoUrlsHighRes)) {
    photos = basicData.photoUrlsHighRes.map((photo: any) => photo.url).filter(Boolean);
  }
  
  // PRIORITY 2: Try regular photoUrls if no high-res photos
  if (photos.length === 0 && basicData.photoUrls && Array.isArray(basicData.photoUrls)) {
    photos = basicData.photoUrls.map((photo: any) => photo.url).filter(Boolean);
  }
  
  // PRIORITY 3: Try media high-resolution photos
  if (photos.length === 0 && basicData.media) {
    if (basicData.media.allPropertyPhotos && 
        basicData.media.allPropertyPhotos.highResolution && 
        Array.isArray(basicData.media.allPropertyPhotos.highResolution)) {
      photos = basicData.media.allPropertyPhotos.highResolution;
    } else if (basicData.media.propertyPhotoLinks && 
               basicData.media.propertyPhotoLinks.highResolutionLink) {
      photos = [basicData.media.propertyPhotoLinks.highResolutionLink];
    }
  }
  
  // Fallback to other photo fields
  if (photos.length === 0) {
    if (Array.isArray(basicData.photos)) {
      photos = basicData.photos;
    } else if (Array.isArray(basicData.images)) {
      photos = basicData.images;
    } else if (basicData.photo && typeof basicData.photo === 'string') {
      photos = [basicData.photo];
    }
  }
  
  // Extract description - address search has rich description
  let description = filterZestimateDescriptions(basicData.description || 'Property details available');
  
  // Add additional details if available
  if (basicData.whatILove) {
    description += ` ${basicData.whatILove}`;
  }
  
  // Extract URL
  let url = basicData.url || basicData.zillowUrl || '';
  if (basicData.hdpView && basicData.hdpView.hdpUrl) {
    url = 'https://www.zillow.com' + basicData.hdpView.hdpUrl;
  }
  
  // Extract property type and normalize to UI label
  let propertyType = basicData.resoFacts?.homeType || 
                     basicData.homeType || 
                     basicData.propertyType || 
                     'Unknown';
  // Map uppercase Zillow enums to UI labels
  const TYPE_MAP: Record<string, string> = {
    'SINGLE_FAMILY': 'Single family home',
    'CONDO': 'Condo',
    'TOWNHOME': 'Townhome',
    'TOWNHOUSE': 'Townhome',
    'MULTI_FAMILY': 'Multi family home',
    'MANUFACTURED': 'Mobile',
    'LAND': 'Land',
    'APARTMENT': 'Apartment',
  };
  if (TYPE_MAP[propertyType]) {
    propertyType = TYPE_MAP[propertyType];
  } else {
    // Also handle lowercase API values
    const lcMap: Record<string, string> = {
      'singleFamily': 'Single family home',
      'condo': 'Condo',
      'townhome': 'Townhome',
      'townhouse': 'Townhome',
      'multiFamily': 'Multi family home',
      'manufactured': 'Mobile',
      'land': 'Land',
      'apartment': 'Apartment',
    };
    propertyType = lcMap[propertyType] || propertyType;
  }
  
  
  // Extract status - prefer marketingStatus if available for accurate pending/contingent detection
  const status = (basicData.listing?.marketingStatus || basicData.homeStatus || basicData.status || 'forSale')
                  .toString()
                  .toLowerCase();
  
  // Extract location coordinates
  let lat = 0;
  let lon = 0;
  if (basicData.latitude && basicData.longitude) {
    lat = basicData.latitude;
    lon = basicData.longitude;
  } else if (basicData.lat && basicData.lng) {
    lat = basicData.lat;
    lon = basicData.lng;
  } else if (basicData.location && typeof basicData.location === 'object') {
    lat = basicData.location.lat || basicData.location.latitude || 0;
    lon = basicData.location.lon || basicData.location.longitude || 0;
  }
  

  
  // Remove virtual tour detection entirely
  
  // Create property object
  const property: Property = {
    id: zpid || `zillow-${Date.now()}-${Math.random()}`,
    title: '', // Don't store titles for Buy properties - this prevents duplicate addresses
    address: address,
    city: city,
    state: state,
    zipCode: zipCode,
    price: price,
    details: {
      bedrooms: bedrooms,
      bathrooms: bathrooms,
      squareFeet: squareFeet,
      yearBuilt: yearBuilt,
      lotSize: { amount: lotSize },
    },
    type: propertyType,
    status: status,
    photos: photos,
    description: description,
    location: {
      lat: lat,
      lon: lon,
    },
    url: url,
    last_sync: new Date().toISOString(),
    source: 'zillow',
    source_id: String(zpid),
    
    lot_area_units: lotSizeUnit || basicData.lotAreaUnits || basicData.lotAreaUnit || '', // Add lot size units
  };
  

  // Map HOA dues if present (compute monthly when frequency provided)
  try {
    const monthlyHoaFee = basicData.monthlyHoaFee;
    const hoaFee = basicData.hoaFee;
    const hoaFeeFrequency = basicData.hoaFeeFrequency || basicData.hoa_fee_frequency;
    let amount: number | null = null;
    let frequency: string | null = null;
    if (typeof monthlyHoaFee === 'number') {
      amount = monthlyHoaFee;
      frequency = 'Monthly';
    } else if (typeof hoaFee === 'number') {
      // Convert to monthly amount based on frequency
      const freq = (hoaFeeFrequency || '').toString().toLowerCase();
      frequency = hoaFeeFrequency || null;
      if (freq.includes('monthly')) amount = hoaFee;
      else if (freq.includes('quarter')) amount = Math.round((hoaFee / 3) * 100) / 100;
      else if (freq.includes('semi')) amount = Math.round((hoaFee / 6) * 100) / 100;
      else if (freq.includes('annual') || freq.includes('year')) amount = Math.round((hoaFee / 12) * 100) / 100;
      else amount = hoaFee; // Unknown frequency; treat as monthly
    }
    if (amount !== null) {
      (property as any).hoa_dues = { amount, frequency };
    }
  } catch {}


  
  // Add additional address search specific fields if available
  if (basicData.zestimate) {
    (property as any).zestimate = basicData.zestimate;
  }
  
  if (basicData.pricePerSquareFoot) {
    (property as any).pricePerSquareFoot = basicData.pricePerSquareFoot;
  }
  
  // Normalize days on market from multiple possible sources
  try {
    const extractDays = (val: any): number | null => {
      if (val === null || val === undefined) return null;
      if (typeof val === 'number' && Number.isFinite(val)) return val;
      if (typeof val === 'string') {
        const m = val.match(/\d+/);
        return m ? parseInt(m[0], 10) : null;
      }
      return null;
    };

    const dom = (
      extractDays(basicData.daysOnMarket) ??
      extractDays(basicData.days_on_market) ??
      extractDays(basicData.daysOnZillow) ??
      extractDays(basicData.timeOnZillow)
    );
    if (dom !== null) {
      (property as any).daysOnMarket = dom;
    }
  } catch {}
  
  if (basicData.timeOnZillow) {
    (property as any).timeOnZillow = basicData.timeOnZillow;
  }
  
  if (basicData.time_on_zillow) {
    (property as any).time_on_zillow = basicData.time_on_zillow;
  }

  return property;
}

/**
 * Extract property data from location search response (original logic)
 */
async function extractPropertyFromLocationData(zpid: string, basicData: any): Promise<Property> {
  // Extract address information
  let address = '';
  let city = '';
  let state = '';
  let zipCode = '';

  if (typeof basicData.address === 'string') {
    address = basicData.address;
  } else if (basicData.address && typeof basicData.address === 'object') {
    address = basicData.address.streetAddress || basicData.address.address || '';
    city = basicData.address.city || '';
    state = basicData.address.state || '';
    zipCode = basicData.address.zipcode || basicData.address.zipCode || '';
  } else {
    address = basicData.streetAddress || basicData.address || '';
    city = basicData.city || '';
    state = basicData.state || '';
    zipCode = basicData.zipcode || basicData.zipCode || '';
  }

  // Extract price information
  let price = 0;
  if (typeof basicData.price === 'number') {
    price = basicData.price;
  } else if (basicData.price && typeof basicData.price === 'object') {
    price = basicData.price.value || 0;
  } else {
    price = basicData.listPrice || basicData.price || 0;
  }

  // Extract location coordinates
  let lat = 0;
  let lon = 0;
  if (basicData.latitude && basicData.longitude) {
    lat = basicData.latitude;
    lon = basicData.longitude;
  } else if (basicData.lat && basicData.lng) {
    lat = basicData.lat;
    lon = basicData.lng;
  } else if (basicData.location && typeof basicData.location === 'object') {
    lat = basicData.location.lat || basicData.location.latitude || 0;
    lon = basicData.location.lon || basicData.location.longitude || 0;
  }



  // Extract property details
  const bedrooms = basicData.bedrooms || basicData.beds || 0;
  const bathrooms = basicData.bathrooms || basicData.baths || 0;
  const squareFeet = basicData.livingArea || basicData.squareFeet || 0;
  const yearBuilt = basicData.yearBuilt || 0;
  
  // Extract lot size
  let lotSize: any = 0;
  let lotSizeUnit = '';
  
  if (basicData.lotSizeWithUnit?.lotSize) {
    lotSize = basicData.lotSizeWithUnit.lotSize;
    lotSizeUnit = basicData.lotSizeWithUnit.lotSizeUnit || '';
  } else if (basicData.lotArea) {
    lotSize = basicData.lotArea;
  } else if (basicData.lotSize) {
    lotSize = basicData.lotSize;
  } else if (basicData.lotSizeWithUnit) {
    lotSize = basicData.lotSizeWithUnit;
  }
  
  // Round the lot size to reasonable precision
  lotSize = roundLotSize(lotSize);

  // Extract photos - prioritize high-resolution photos
  let photos: string[] = [];
  
  // PRIORITY 1: Try photoUrlsHighRes first for high-resolution photos
  if (basicData.photoUrlsHighRes && Array.isArray(basicData.photoUrlsHighRes)) {
    photos = basicData.photoUrlsHighRes.map((photo: any) => photo.url).filter(Boolean);
  }
  
  // PRIORITY 2: Try regular photoUrls if no high-res photos
  if (photos.length === 0 && basicData.photoUrls && Array.isArray(basicData.photoUrls)) {
    photos = basicData.photoUrls.map((photo: any) => photo.url).filter(Boolean);
  }
  
  // PRIORITY 3: Try media high-resolution photos
  if (photos.length === 0 && basicData.media) {
    if (basicData.media.allPropertyPhotos && 
        basicData.media.allPropertyPhotos.highResolution && 
        Array.isArray(basicData.media.allPropertyPhotos.highResolution)) {
      photos = basicData.media.allPropertyPhotos.highResolution;
    } else if (basicData.media.propertyPhotoLinks && 
               basicData.media.propertyPhotoLinks.highResolutionLink) {
      photos = [basicData.media.propertyPhotoLinks.highResolutionLink];
    } else if (basicData.media.thirdPartyPhotoLinks && 
               Array.isArray(basicData.media.thirdPartyPhotoLinks)) {
      photos = basicData.media.thirdPartyPhotoLinks;
    }
  }
  
  // Fallback to old logic
  if (photos.length === 0) {
    if (Array.isArray(basicData.photos)) {
      photos = basicData.photos;
    } else if (Array.isArray(basicData.images)) {
      photos = basicData.images;
    } else if (basicData.photo && typeof basicData.photo === 'string') {
      photos = [basicData.photo];
    }
  }

  // Extract description
  let description = filterZestimateDescriptions(basicData.description || 'Property details available');
  
  // Extract URL
  let url = basicData.url || basicData.zillowUrl || '';
  if (basicData.hdpView && basicData.hdpView.hdpUrl) {
    url = 'https://www.zillow.com' + basicData.hdpView.hdpUrl;
  }

  // Remove virtual tour detection entirely

  // Create property object
  const property: Property = {
    id: zpid || `zillow-${Date.now()}-${Math.random()}`,
    title: '', // Don't store titles for Buy properties - this prevents duplicate addresses
    address: address,
    city: city,
    state: state,
    zipCode: zipCode,
    price: price,
    details: {
      bedrooms: bedrooms,
      bathrooms: bathrooms,
      squareFeet: squareFeet,
      yearBuilt: yearBuilt,
      lotSize: { amount: lotSize },
    },
    type: (() => {
      const rawType = basicData.propertyType || basicData.homeType || 'Unknown';
      const TYPE_MAP: Record<string, string> = {
        'SINGLE_FAMILY': 'Single family home',
        'CONDO': 'Condo',
        'TOWNHOME': 'Townhome',
        'TOWNHOUSE': 'Townhome',
        'MULTI_FAMILY': 'Multi family home',
        'MANUFACTURED': 'Mobile',
        'LAND': 'Land',
        'APARTMENT': 'Apartment',
        'singleFamily': 'Single family home',
        'condo': 'Condo',
        'townhome': 'Townhome',
        'townhouse': 'Townhome',
        'multiFamily': 'Multi family home',
        'manufactured': 'Mobile',
        'land': 'Land',
        'apartment': 'Apartment',
      };
      return TYPE_MAP[rawType] || rawType;
    })(),
    // Prefer marketingStatus for accurate state (e.g., pending/contingent), fallback to listingStatus/status
    status: (basicData.listing?.marketingStatus || basicData.listing?.listingStatus || basicData.status || 'forSale')
              .toString()
              .toLowerCase(),
    photos: photos,
    description: description,
    location: {
      lat: lat,
      lon: lon,
    },
    url: url,
    last_sync: new Date().toISOString(),
    source: 'zillow',
    source_id: String(zpid),
    
    lot_area_units: lotSizeUnit, // Add lot size units
    openHouses: extractOpenHouseData(basicData), // Extract open house data
  };

  // Normalize and attach days on market from location search payload
  try {
    const extractDays = (val: any): number | null => {
      if (val === null || val === undefined) return null;
      if (typeof val === 'number' && Number.isFinite(val)) return val;
      if (typeof val === 'string') {
        const m = val.match(/\d+/);
        return m ? parseInt(m[0], 10) : null;
      }
      return null;
    };
    const dom = (
      extractDays((basicData as any).daysOnMarket) ??
      extractDays((basicData as any).days_on_market) ??
      extractDays((basicData as any).daysOnZillow) ??
      extractDays((basicData as any).timeOnZillow)
    );
    if (dom !== null) {
      (property as any).daysOnMarket = dom;
    }
  } catch {}

  // Map HOA dues if present (compute monthly when frequency provided)
  try {
    const monthlyHoaFee = (basicData as any).monthlyHoaFee || (basicData as any).monthly_hoa_fee;
    const hoaFee = (basicData as any).hoaFee || (basicData as any).hoa_fee;
    const hoaFeeFrequency = (basicData as any).hoaFeeFrequency || (basicData as any).hoa_fee_frequency;
    let amount: number | null = null;
    let frequency: string | null = null;
    if (typeof monthlyHoaFee === 'number') {
      amount = monthlyHoaFee;
      frequency = 'Monthly';
    } else if (typeof hoaFee === 'number') {
      const freq = (hoaFeeFrequency || '').toString().toLowerCase();
      frequency = hoaFeeFrequency || null;
      if (freq.includes('monthly')) amount = hoaFee;
      else if (freq.includes('quarter')) amount = Math.round((hoaFee / 3) * 100) / 100;
      else if (freq.includes('semi')) amount = Math.round((hoaFee / 6) * 100) / 100;
      else if (freq.includes('annual') || freq.includes('year')) amount = Math.round((hoaFee / 12) * 100) / 100;
      else amount = hoaFee;
    }
    if (amount !== null) {
      (property as any).hoa_dues = { amount, frequency };
    }
  } catch {}

  return property;
}

// Removed lazy loading for virtual tours

/**
 * Step 1: Get location from auto-complete endpoint
 */
async function getLocationFromAutoComplete(userInput: string, signal?: AbortSignal): Promise<string> {
  // Check cache first
  const cacheKey = userInput.toLowerCase().trim();
  const cached = autoCompleteCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.result;
  }

  const maxRetries = 1;
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const autoCompleteUrl = '/api/properties/auto-complete';
      const params = new URLSearchParams();
      params.append('query', userInput);

      const fullUrl = `${autoCompleteUrl}?${params.toString()}`;

      const response = await enhancedFetch(
        fullUrl,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal,
        }
      );

      const data = await response.json();
      
      // Extract location from Zillow auto-complete response
      if (data.data && Array.isArray(data.data)) {
        const firstResult = data.data.find((item: any) => item.display);
        if (firstResult) {
          const location = firstResult.display;
          autoCompleteCache.set(cacheKey, { result: location, timestamp: Date.now() });
          return location;
        }
      }

      // Try alternative response structures
      if (data.results && Array.isArray(data.results)) {
        const firstResult = data.results.find((item: any) => item.display || item.text);
        if (firstResult) {
          const location = firstResult.display || firstResult.text;
          autoCompleteCache.set(cacheKey, { result: location, timestamp: Date.now() });
          return location;
        }
      }

      if (data.suggestions && Array.isArray(data.suggestions)) {
        const firstSuggestion = data.suggestions.find((item: any) => item.text || item.display);
        if (firstSuggestion) {
          const location = firstSuggestion.text || firstSuggestion.display;
          autoCompleteCache.set(cacheKey, { result: location, timestamp: Date.now() });
          return location;
        }
      }

      throw new Error('No location found in auto-complete response');
    } catch (error) {
      lastError = error as Error;
      
      if (attempt < maxRetries) {
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error('Auto-complete failed after all retry attempts');
}

/**
 * Step 2: Search properties using the appropriate Zillow API endpoint
 */
async function searchPropertiesWithLocation(params: SalePropertySearchParams, _signal?: AbortSignal): Promise<{zpid: string, basicData: any}[]> {
  const maxRetries = 1;
  let lastError: Error | null = null;

  if (!params.location || params.location.trim() === '') {
    throw new Error('Location parameter is required and cannot be empty');
  }

  // Detect if this is an address-based search (contains street number and address)
  // Pattern: starts with number + space, and contains comma or state abbreviation or street patterns
  const isAddressSearch = /^\d+\s+/.test(params.location.trim()) && 
                         (params.location.includes(',') || 
                          /[A-Z]{2}/.test(params.location) || 
                          /\b(?:Rd|Road|St|Street|Ave|Avenue|Blvd|Boulevard|Dr|Drive|Ln|Lane|Ct|Court|Pl|Place|Way|Cir|Circle)\b/i.test(params.location));

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      if (isAddressSearch) {
        return await searchPropertiesByAddress(params.location.trim());
      } else {
        return await searchPropertiesByLocation(params.location.trim(), params);
      }
    } catch (error) {
      lastError = error as Error;
      
      if (attempt < maxRetries) {
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error('Property search failed after all retry attempts');
}

/**
 * Try to search for a property using a specific address format
 */
async function tryAddressSearch(addressFormat: string, _originalAddress: string, _signal?: AbortSignal): Promise<{zpid: string, basicData: any}[] | null> {
  // Minimal delay between attempts to avoid rate limiting but keep it fast
  await new Promise(resolve => setTimeout(resolve, 50));
  
  const searchUrl = '/api/properties/search-address';
  const searchParams = new URLSearchParams();
  
  searchParams.append('address', addressFormat);
  
  const fullUrl = `${searchUrl}?${searchParams.toString()}`;

  try {
    const response = await enhancedFetch(
      fullUrl,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        _signal,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      
      // Check if it's a Zillow validation error
      if (response.status === 400 && errorText.includes('not a detailed address')) {
        return null; // Try next format
      }
      
      // Check for rate limiting
      if (response.status === 429) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return null; // Try next format
      }
      
      throw new Error(`Address search failed: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    
    if (!data.data) {
      return null; // Try next format
    }
    
    // Address search returns a single property, so we wrap it in an array
    const propertyData = data.data;
    const zpid = propertyData.zpid || propertyData.id;
    
    if (zpid) {

      // Extract additional fields that will be used later in property creation
      const enhancedBasicData = {
        ...propertyData,
        // Ensure daysOnZillow is available for property creation
        daysOnZillow: propertyData.daysOnZillow || null,
        // Add other useful fields
        pricePerSquareFoot: propertyData.pricePerSquareFoot || null,
        zestimate: propertyData.zestimate || null,
        // Check for alternative field names for days on market
        daysOnMarket: propertyData.daysOnMarket || null,
        days_on_market: propertyData.days_on_market || null,
        timeOnZillow: propertyData.timeOnZillow || null,
        time_on_zillow: propertyData.time_on_zillow || null
      };
      
      return [{
        zpid: String(zpid),
        basicData: enhancedBasicData
      }];
    }
    
    return null; // Try next format
  } catch {
    throw new Error('Property search by location failed');
  }
}

/**
 * Search properties by specific address using the address endpoint with retry logic
 */
async function searchPropertiesByAddress(address: string, _signal?: AbortSignal): Promise<{zpid: string, basicData: any}[]> {
  // Try different address formats to improve success rate
  const addressFormats = [
    address, // Try original address first
    address.replace(/,\s*\d{5}(?:-\d{4})?$/, ''), // Without zip
    address.split(',').slice(0, 2).join(',') // Just street and city
  ];
  
  // Track processed addresses to avoid duplicates
  const processedAddresses = new Set<string>();
  
  for (let i = 0; i < addressFormats.length; i++) {
    const currentFormat = addressFormats[i];
    
    // Skip if we've already processed this exact format
    if (processedAddresses.has(currentFormat)) {
      continue;
    }
    
    processedAddresses.add(currentFormat);
    
    try {
      const result = await tryAddressSearch(currentFormat, address, _signal);
      if (result) {
        return result;
      }
    } catch {
      if (i === addressFormats.length - 1) {
        throw new Error('Address search failed');
      }
    }
  }
  
  // If all formats fail, return empty array
  return [];
}

/**
 * Search properties by location using the location endpoint
 */
async function searchPropertiesByLocation(location: string, params: SalePropertySearchParams, _signal?: AbortSignal): Promise<{zpid: string, basicData: any}[]> {
  try {
    const searchUrl = '/api/properties/search';
    const searchParams = new URLSearchParams();
    
    searchParams.append('location', location);
    
    if (params.page !== undefined) searchParams.append('page', (params.page + 1).toString()); // Convert 0-based to 1-based indexing
    if (params.resultsPerPage) searchParams.append('resultsPerPage', params.resultsPerPage.toString());
    if (params.status) searchParams.append('status', params.status);
    if (params.sort) searchParams.append('sort', params.sort);

    const fullUrl = `${searchUrl}?${searchParams.toString()}`;

    const response = await enhancedFetch(
      fullUrl,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        _signal,
      }
    );

    const data = await response.json();

    
    const propertiesWithZpids: {zpid: string, basicData: any}[] = [];
    
    let propertyDataArray: any[] = [];
    
    if (data.data && Array.isArray(data.data)) {
      propertyDataArray = data.data;
    } else if (data.results && Array.isArray(data.results)) {
      propertyDataArray = data.results;
    } else if (data.properties && Array.isArray(data.properties)) {
      propertyDataArray = data.properties;
    } else if (data.listings && Array.isArray(data.listings)) {
      propertyDataArray = data.listings;
    } else {
      for (const [, value] of Object.entries(data)) {
        if (Array.isArray(value) && value.length > 0) {
          propertyDataArray = value;
          break;
        }
      }
    }
    
    if (propertyDataArray.length === 0) {
      return propertiesWithZpids;
    }
    
    for (const propertyData of propertyDataArray) {
      try {
        if (!propertyData) {
          continue;
        }

        // Gate: only include for-sale results in Buy flow
        // Since we're specifically requesting status: 'forSale' from the API, 
        // we should be more permissive and only exclude explicitly non-sale properties
        const rawStatus = (propertyData.homeStatus || propertyData.status || propertyData.listing?.marketingStatus || '').toString().toLowerCase();
        const isExplicitlyNotForSale = rawStatus.includes('rent') || rawStatus.includes('sold') || rawStatus.includes('pending') || rawStatus.includes('contingent');
        
        if (isExplicitlyNotForSale) {
          continue;
        }

        const zpid = propertyData.zpid || propertyData.id;
        if (!zpid) {
          continue;
        }


        propertiesWithZpids.push({
          zpid: String(zpid),
          basicData: propertyData
        });
      } catch {
        // Continue processing other properties
      }
    }

    return propertiesWithZpids;
  } catch {
    throw new Error('Property search failed');
  }
}

/**
 * OPTIMIZATION 3: Progressive Loading - Load remaining properties in background
 */
async function loadRemainingProperties(propertiesWithZpids: {zpid: string, basicData: any}[]): Promise<Property[]> {
  if (propertiesWithZpids.length === 0) return [];
  
  try {
    // Process remaining properties in smaller chunks for faster processing
    const chunkSize = 3; // Reduced from 5 to 3 for faster processing
    const chunks = [];
    
    for (let i = 0; i < propertiesWithZpids.length; i += chunkSize) {
      chunks.push(propertiesWithZpids.slice(i, i + chunkSize));
    }
    
    const allProperties: Property[] = [];
    
    for (const chunk of chunks) {
      const chunkPropertiesPromises = chunk.map(async ({ zpid, basicData }) => {
        const property = await extractPropertyFromSearchData(zpid, basicData);
        // Virtual tours are already deferred to lazy loading
        return property;
      });
      
      const chunkProperties = await Promise.all(chunkPropertiesPromises);
      allProperties.push(...chunkProperties);
      
      // Minimal delay between chunks for faster processing
      if (chunks.indexOf(chunk) < chunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 50)); // Reduced from 100ms to 50ms
      }
    }
    
    return allProperties;
  } catch {
    return [];
  }
}

/**
 * OPTIMIZATION 4: Non-blocking progressive loading - Load remaining properties in background
 */
function loadRemainingPropertiesNonBlocking(
  propertiesWithZpids: {zpid: string, basicData: any}[], 
  searchCacheKey: string,
  onProgress?: (initialResults: Property[], totalCount: number) => void,
  searchParams?: SalePropertySearchParams,
  rawUserInput?: string
): void {
  if (propertiesWithZpids.length === 0) return;
  
  // Use a microtask to avoid blocking the current execution
  Promise.resolve().then(async () => {
    try {
      const remainingProperties = await loadRemainingProperties(propertiesWithZpids);
      
      if (remainingProperties.length > 0) {
        // Get current cached results and combine with new ones
        const currentResults = propertyCache.get(searchCacheKey) || [];
        const allProperties = [...currentResults, ...remainingProperties];
        
        // Update cache with all properties using unified cache
        (allProperties as any).timestamp = Date.now();
        // Use centralized cache function
        cacheSaleProperties(allProperties, rawUserInput, undefined, searchParams);
        
        // Cache remaining properties using centralized cache function
        cacheSaleProperties(remainingProperties, rawUserInput, undefined, searchParams);
        
        // Update progress callback
        if (onProgress) {
          onProgress(allProperties, allProperties.length);
        }
        
      }
    } catch {
      // Background loading failed
    }
  });
}

/**
 * Main function to search sale properties with all optimizations
 */
export const searchSaleProperties = throttleSearch(async (
  userInput: string, 
  searchParams: Partial<SalePropertySearchParams> = {},
  onProgress?: (initialResults: Property[], totalCount: number) => void,
  forceRefresh: boolean = false
): Promise<Property[]> => {
  const SEARCH_TIMEOUT_MS = 10000;
  const controller = new AbortController();
  const { signal } = controller;

  const cooldownKey = `${userInput.trim().toLowerCase()}|sale`;
  const nowTs = Date.now();
  const lastTimeoutAt = searchTimeoutCooldown.get(cooldownKey) || 0;
  if (nowTs - lastTimeoutAt < 12000) {
    // Within cooldown window, avoid re-hitting network
    return [];
  }
  return await new Promise<Property[]>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      try {
        controller.abort();
        searchTimeoutCooldown.set(cooldownKey, Date.now());
      } catch {}
      resolve([]);
    }, SEARCH_TIMEOUT_MS);

    (async () => {
      try {
        let searchParamsWithLocation: SalePropertySearchParams = {
          location: userInput.trim(),
          status: 'forSale',
          ...searchParams
        };
        let location: string;
        let propertiesWithZpids: {zpid: string, basicData: any}[] = [];
        const searchCacheKey = generateCacheKey(searchParamsWithLocation, 'sale');
        
        const cachedSearch = propertyCache.get(searchCacheKey);
        const cacheAge = cachedSearch ? Date.now() - (cachedSearch as any).timestamp : Infinity;
        const cacheIsRecent = cacheAge < 30 * 60 * 1000;
        
        if (cachedSearch && cacheIsRecent && !forceRefresh) {
          if (onProgress) {
            onProgress(cachedSearch, cachedSearch.length);
          }
          return cachedSearch;
        } else if (cachedSearch) {
          propertyCache.clear(searchCacheKey);
        }
        if (forceRefresh) {
          propertyCache.clear(searchCacheKey);
        }
        try {
          const [locationResult, searchResult] = await Promise.all([
            getLocationFromAutoComplete(userInput, signal).catch(() => {
              return userInput.trim();
            }),
            searchPropertiesWithLocation({
              location: userInput.trim(),
              status: 'forSale',
              ...searchParams
            }, signal).catch(() => {
              return [];
            })
          ]);
          location = locationResult;
          propertiesWithZpids = searchResult;
          searchParamsWithLocation.location = location;
        } catch {
          location = userInput.trim();
          if (location.length === 2 && /^[A-Z]{2}$/.test(location.toUpperCase())) {
            const stateMap: Record<string, string> = {
              'OK': 'Oklahoma',
              'TX': 'Texas',
              'CA': 'California',
              'NY': 'New York',
              'FL': 'Florida',
            };
            location = stateMap[location.toUpperCase()] || location;
          }
          searchParamsWithLocation.location = location;
          propertiesWithZpids = await searchPropertiesWithLocation(searchParamsWithLocation, signal);
        }
        if (propertiesWithZpids.length === 0) {
          clearTimeout(timeoutId);
          resolve([]);
          return;
        }
        if (propertiesWithZpids.length === 1) {
          const property = await extractPropertyFromSearchData(propertiesWithZpids[0].zpid, propertiesWithZpids[0].basicData);
          const allProperties = [property];
          if (onProgress) {
            onProgress(allProperties, 1);
          }
          if (allProperties.length > 0) {
            cacheSaleProperties(allProperties, userInput.trim(), undefined, searchParamsWithLocation);
          }
          (allProperties as any).timestamp = Date.now();
          clearTimeout(timeoutId);
          resolve(allProperties);
          return;
        } else {
          const initialBatchSize = Math.min(
            propertiesWithZpids.length,
            (searchParams.resultsPerPage as number | undefined) ?? 25
          );
          const initialPropertiesWithZpids = propertiesWithZpids.slice(0, initialBatchSize);
          const remainingPropertiesWithZpids = propertiesWithZpids.slice(initialBatchSize);
          const initialProperties = await Promise.all(
            initialPropertiesWithZpids.map(async ({ zpid, basicData }) => {
              const property = await extractPropertyFromSearchData(zpid, basicData);
              return property;
            })
          );
          if (onProgress) {
            onProgress(initialProperties, propertiesWithZpids.length);
          }
          // Cache and resolve immediately with the initial batch to avoid false timeouts
          (initialProperties as any).timestamp = Date.now();
          if (initialProperties.length > 0) {
            const paramsForCache: SalePropertySearchParams = {
              location: userInput.trim(),
              status: 'forSale',
              ...searchParams
            };
            cacheSaleProperties(initialProperties, userInput.trim(), undefined, paramsForCache);
          }
          if (remainingPropertiesWithZpids.length > 0) {
            const searchCacheKeyNext = generateCacheKey(searchParamsWithLocation, 'sale');
            loadRemainingPropertiesNonBlocking(remainingPropertiesWithZpids, searchCacheKeyNext, onProgress, searchParamsWithLocation, userInput.trim());
          }
          clearTimeout(timeoutId);
          resolve(initialProperties);
          return;
        }
      } catch {
        clearTimeout(timeoutId);
        reject(new Error('Search failed'));
      }
    })();
  });
}, 100);

/**
 * Search sale properties by location parameters
 */
export const searchSalePropertiesByLocation = throttleSearch(async (params: SearchParams): Promise<Property[]> => {
  try {
    const { city, state, zipCode, sort, ...otherParams } = params;
    
    let location = '';
    if (city && state) {
      location = `${city}, ${state}`;
    } else if (city) {
      location = city;
    } else if (state) {
      location = state;
    } else if (zipCode) {
      location = zipCode;
    } else {
      throw new Error('At least one location parameter is required');
    }
    
    let zillowSort: SalePropertySearchParams['sort'] = 'relevance';
    if (sort === 'relevant') zillowSort = 'relevance';
    else if (sort === 'price_asc') zillowSort = 'price';
    else if (sort === 'price_desc') zillowSort = 'price';
    
    const searchParams: SalePropertySearchParams = {
      location,
      status: 'forSale',
      sort: zillowSort,
      sortType: sort === 'price_asc' ? 'asc' : 'desc',
      ...otherParams
    };
    
    const searchCacheKey = generateCacheKey(searchParams, 'sale');
    const cachedSearch = propertyCache.get(searchCacheKey);
    if (cachedSearch) {
      return cachedSearch;
    }
    
    const propertiesWithZpids = await searchPropertiesWithLocation(searchParams);
    
    const propertiesPromises = propertiesWithZpids.map(async ({ zpid, basicData }) => 
      extractPropertyFromSearchData(zpid, basicData)
    );
    const properties = await Promise.all(propertiesPromises);
    
    // Virtual tours are loaded lazily when user clicks 3D tour button via getVirtualTourUrl()
    if (properties.length > 0) {
      // Use centralized cache function
      cacheSaleProperties(properties, location, undefined, searchParams);
    }
    
    // Cache is already handled by cachePropertiesUnified above
    
    return properties;
  } catch {
    throw new Error('Property search by location failed');
  }
}, 200);